import Vue from 'vue'
import { ServiceInjector } from '@/service/registry/ServiceInjector'

/**
 * Service Registry Container class
 * Creates and maintains Observable/Reactive Stateful Services
 * for use outside and within Vue components
 */
export class ServiceRegistryConstructor extends Object {
  #services = {}

  /**
   * Internal method for creating services that extend ServiceInjector class
   * @template T
   * @param {Class<T>} ServiceClass takes a prototype of an ES6 class
   */
  registerInjectableService (ServiceClass) {
    this._isServiceInjectorClassCheck(ServiceClass)
    const serviceName = ServiceClass.injectedName()
    if (!this.#services[serviceName]) {
      this.#services[serviceName] = Vue.observable(new ServiceClass()).lock
      console.debug('Registered', serviceName)
    }
  }

  /**
   * Public injector method for fetching services based on the name
   * @template T
   * @param {Class<T>} ServiceClass
   * @returns {T}
   */
  inject (ServiceClass) {
    this.registerInjectableService(ServiceClass)
    return this.#services[ServiceClass.injectedName()]
  }

  /**
   * Used to populate the App provide array with the generated Services
   * @returns {Object}
   */
  get provideServices () {
    return this.#services
  }

  /**
   * Internal method for creating services that extend ServiceInjector class
   * @private
   * @template T
   * @param {Class<T>} ServiceClass takes a prototype of an ES6 class
   */
  _isServiceInjectorClassCheck (ServiceClass) {
    if (ServiceClass instanceof Function && ServiceClass.prototype instanceof Object) {
      if (ServiceInjector.isPrototypeOf(ServiceClass)) {
        return
      }
      throw new Error(`Provided class ${ServiceClass.name} is not a prototype of ServiceInjector`)
    }
    throw new Error(`Expected ES6 Class that extends ServiceInjector received : ${ServiceClass}`)
  }
}

export default new ServiceRegistryConstructor()
